import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { publicApi } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_groove-tours/artifacts/bqmuqiqz_IMG-20260208-WA0097.jpg";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    publicApi.getSettings().then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const navLinks = [
    { name: t('nav_inicio'), path: '/' },
    { name: t('nav_excursiones'), path: '/excursiones' },
    { name: t('nav_reservar'), path: '/reservar' },
    { name: t('nav_admin'), path: '/admin' },
    { name: t('nav_contacto'), path: '/contacto' },
  ];

  const whatsappNumber = settings?.contact?.whatsapp?.replace(/\D/g, '') || '18091234567';
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, estoy interesado en sus excursiones.')}`;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className={`fixed top-4 left-1/2 -translate-x-1/2 w-[95%] max-w-5xl z-50 rounded-full px-4 md:px-6 py-3 transition-all duration-300 ${
        scrolled 
          ? 'glass shadow-lg' 
          : 'bg-white/60 backdrop-blur-md border border-white/40'
      }`}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src={LOGO_URL} 
              alt="Caribbean Groove Tours" 
              className="w-12 h-12 rounded-full object-cover"
            />
            <span className="hidden sm:block font-semibold text-primary text-sm md:text-base">
              Caribbean Groove
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-stone-600 hover:text-primary hover:bg-primary/5'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Language Selector & WhatsApp Button */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Button 
                className="rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2 whatsapp-pulse"
                data-testid="navbar-whatsapp-btn"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden lg:inline">{t('nav_whatsapp')}</span>
                <span className="lg:hidden">WhatsApp</span>
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-full hover:bg-stone-100 transition-colors"
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
          <div className="absolute top-20 left-4 right-4 glass rounded-2xl p-4 shadow-xl mobile-menu-enter">
            {/* Mobile Language Selector */}
            <div className="flex justify-center mb-3 pb-3 border-b border-stone-200">
              <LanguageSelector />
            </div>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-stone-600 hover:bg-stone-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a 
                href={whatsappUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-2"
                onClick={() => setIsOpen(false)}
              >
                <Button className="w-full rounded-xl bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2 py-6">
                  <Phone className="w-5 h-5" />
                  {t('nav_whatsapp')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
